// AI-CONTEXT: See docs/features/participation-ladder.md
import { useContext } from 'react'
import { db } from '../../../firebase/config'
import firebase from '../../../firebase/compat'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import {
    MAIN_ACTIVITY_OPTIONS,
    HOURS_BAND_OPTIONS,
    EMPLOYABILITY_OPTIONS,
    EDUCATION_LEVEL_OPTIONS,
    VOLUNTEER_HOURS_OPTIONS,
    BENEFIT_SHARE_OPTIONS,
    INACTIVITY_LEVEL_OPTIONS,
    PAID_WORK_ACTIVITY,
} from '../../../helpers/participationHelpers/participationOptions'
import getParticipationFormSpec, { displayBenefitType } from '../../../helpers/participationHelpers/getParticipationFormSpec'

// Records the characteristics (kenmerken) for a single moment (t0 or t1),
// following the "Output Participatieladder" questions. Writes go onto the
// persona's Participation map. Welke vragen er staan volgt uit de vastgezette
// parameterset (getParticipationFormSpec): 2026.3 vraagt loonkostensubsidie,
// dagbesteding en het doelniveau van een opleiding, en geen deel van de bijstand.
/**
 * @param {object} props
 * @param {object} props.item - Het persona-document (met `docid` en `Participation`)
 * @param {'t0'|'t1'} props.moment - Welk meetmoment dit formulier bewerkt
 * @param {object|null} props.parameters - De vastgezette parameterset; bepaalt de vragen
 */
const ParticipationProfileForm = ({ item, moment, parameters }) => {
    const [, setSaved] = useContext(SavedIcon)
    const spec = getParticipationFormSpec(parameters)

    const profile = (item.Participation && item.Participation[moment]) || {}
    const isPaidWork = profile.mainActivity === PAID_WORK_ACTIVITY
    const isVolunteer = profile.mainActivity === 'Vrijwilligerswerk'
    const isInactive = profile.mainActivity === 'Niet actief'
    const showEducation = isPaidWork || profile.mainActivity === 'Opleiding'
    const isBijstand = profile.benefitType === 'Bijstandsuitkering'
    const isOpleiding = profile.mainActivity === 'Opleiding'
    const hasDagbesteding = Boolean(profile.dagbesteding) && profile.dagbesteding !== 'Geen'

    const path = (field) => `Participation.${moment}.${field}`

    const update = (field, value) => {
        db.collection('Personas')
            .doc(item.docid)
            .update({
                [path(field)]: value,
                [path('updatedAt')]: firebase.firestore.FieldValue.serverTimestamp(),
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
            .then(() => setSaved('flex'))
    }

    // Changing the benefit clears the share: it only applies to bijstand.
    const benefitTypeHandler = (e) => {
        const value = e.target.value
        db.collection('Personas')
            .doc(item.docid)
            .update({
                [path('benefitType')]: value,
                [path('benefitShare')]: null,
                [path('updatedAt')]: firebase.firestore.FieldValue.serverTimestamp(),
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
            .then(() => setSaved('flex'))
    }

    // Geen dagbesteding wist de dagdelen: die worden alleen bij een soort gevraagd.
    const dagbestedingHandler = (e) => {
        const value = e.target.value
        const updates = {
            [path('dagbesteding')]: value,
            [path('updatedAt')]: firebase.firestore.FieldValue.serverTimestamp(),
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        }
        if (!value || value === 'Geen') updates[path('dagdelenPerWeek')] = null
        db.collection('Personas').doc(item.docid).update(updates).then(() => setSaved('flex'))
    }

    const numberHandler = (field) => (e) => {
        const value = e.target.value
        update(field, value === '' ? null : Number(value))
    }

    // Changing the activity clears fields that no longer apply.
    const mainActivityHandler = (e) => {
        const value = e.target.value
        const updates = {
            [path('mainActivity')]: value,
            [path('updatedAt')]: firebase.firestore.FieldValue.serverTimestamp(),
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        }
        if (value !== PAID_WORK_ACTIVITY) {
            updates[path('hoursBand')] = ''
            updates[path('employabilityFactor')] = null
            updates[path('loonkostensubsidie')] = null
        }
        if (value !== 'Opleiding') {
            updates[path('educationTarget')] = ''
        }
        if (value !== PAID_WORK_ACTIVITY && value !== 'Opleiding') {
            updates[path('educationLevel')] = ''
        }
        if (value !== 'Vrijwilligerswerk') {
            updates[path('volunteerHoursPerWeek')] = null
        }
        if (value !== 'Niet actief') {
            updates[path('inactivityLevel')] = ''
        }
        db.collection('Personas').doc(item.docid).update(updates).then(() => setSaved('flex'))
    }

    return (
        <div className="participation-form">
            <label className="participation-field">
                <span>Wat doe je op dit moment?</span>
                <select value={profile.mainActivity || ''} onChange={mainActivityHandler}>
                    <option value="">-- Kies een activiteit --</option>
                    {MAIN_ACTIVITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            </label>

            {isInactive && (
                <label className="participation-field">
                    <span>Hoe ziet je situatie eruit?</span>
                    <select value={profile.inactivityLevel || ''} onChange={(e) => update('inactivityLevel', e.target.value)}>
                        <option value="">-- Kies situatie --</option>
                        {INACTIVITY_LEVEL_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>
            )}

            {isPaidWork && (
                <label className="participation-field">
                    <span>Hoeveel uur per week?</span>
                    <select value={profile.hoursBand || ''} onChange={(e) => update('hoursBand', e.target.value)}>
                        <option value="">-- Kies urenband --</option>
                        {HOURS_BAND_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>
            )}

            {isPaidWork && (
                <label className="participation-field">
                    <span>Ben je volledig inzetbaar?</span>
                    <select
                        value={Number.isFinite(profile.employabilityFactor) ? profile.employabilityFactor : ''}
                        onChange={numberHandler('employabilityFactor')}
                    >
                        <option value="">-- Kies inzetbaarheid --</option>
                        {EMPLOYABILITY_OPTIONS.map((option) => (
                            <option key={option} value={option}>{Math.round(option * 100)}%</option>
                        ))}
                    </select>
                </label>
            )}

            {isPaidWork && spec.askLoonkostensubsidie && (
                <label className="participation-field">
                    <span>Loonkostensubsidie?</span>
                    <select
                        value={typeof profile.loonkostensubsidie === 'boolean' ? String(profile.loonkostensubsidie) : ''}
                        onChange={(e) => update('loonkostensubsidie', e.target.value === '' ? null : e.target.value === 'true')}
                    >
                        <option value="">-- Kies --</option>
                        <option value="false">Nee</option>
                        <option value="true">Ja</option>
                    </select>
                </label>
            )}

            {showEducation && (
                <label className="participation-field">
                    <span>Hoogst afgeronde opleiding</span>
                    <select value={profile.educationLevel || ''} onChange={(e) => update('educationLevel', e.target.value)}>
                        <option value="">-- Kies opleiding --</option>
                        {EDUCATION_LEVEL_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>
            )}

            {isOpleiding && spec.educationTargetOptions.length > 0 && (
                <label className="participation-field">
                    <span>Naar welk niveau leidt de opleiding?</span>
                    <select value={profile.educationTarget || ''} onChange={(e) => update('educationTarget', e.target.value)}>
                        <option value="">-- Kies niveau --</option>
                        {spec.educationTargetOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>
            )}

            {isVolunteer && (
                <label className="participation-field">
                    <span>Vrijwilligersuren per week</span>
                    <select
                        value={Number.isFinite(profile.volunteerHoursPerWeek) ? profile.volunteerHoursPerWeek : ''}
                        onChange={numberHandler('volunteerHoursPerWeek')}
                    >
                        <option value="">-- Kies uren --</option>
                        {VOLUNTEER_HOURS_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option} uur</option>
                        ))}
                    </select>
                </label>
            )}

            <label className="participation-field">
                <span>Ontvang je een uitkering?</span>
                <select value={displayBenefitType(profile.benefitType, parameters)} onChange={benefitTypeHandler}>
                    <option value="">-- Kies uitkering --</option>
                    {spec.benefitTypeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            </label>

            {isBijstand && spec.askBenefitShare && (
                <label className="participation-field">
                    <span>Welk deel van de volledige bijstand ontvang je?</span>
                    <select
                        value={Number.isFinite(profile.benefitShare) ? profile.benefitShare : ''}
                        onChange={numberHandler('benefitShare')}
                    >
                        <option value="">-- Kies deel --</option>
                        {BENEFIT_SHARE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option === 1 ? 'Volledig' : `Ongeveer ${Math.round(option * 100)}%`}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {spec.dagbestedingOptions.length > 0 && (
                <label className="participation-field">
                    <span>Dagbesteding?</span>
                    <select value={profile.dagbesteding || ''} onChange={dagbestedingHandler}>
                        <option value="">-- Kies dagbesteding --</option>
                        {spec.dagbestedingOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>
            )}

            {hasDagbesteding && spec.dagdelenOptions.length > 0 && (
                <label className="participation-field">
                    <span>Hoeveel dagdelen per week?</span>
                    <select
                        value={Number.isFinite(profile.dagdelenPerWeek) ? profile.dagdelenPerWeek : ''}
                        onChange={numberHandler('dagdelenPerWeek')}
                    >
                        <option value="">-- Kies dagdelen --</option>
                        {spec.dagdelenOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </label>
            )}
        </div>
    )
}

export default ParticipationProfileForm
